import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Play, Search, Loader2, Workflow } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { RoleGate } from "@/components/RoleGate";
import { useSentinelOrgs, useRunEvaluation, useRunAudit } from "@/hooks/useSentinelCycle";
import { SENTINEL_TABS, type SentinelTab } from "@/components/sentinel/sentinel-constants";
import SentinelOverview from "@/components/sentinel/SentinelOverview";
import SentinelAlerts from "@/components/sentinel/SentinelAlerts";
import SentinelAudits from "@/components/sentinel/SentinelAudits";
import SentinelProposals from "@/components/sentinel/SentinelProposals";
import SentinelForecasts from "@/components/sentinel/SentinelForecasts";
import SentinelReports from "@/components/sentinel/SentinelReports";
import SentinelConfig from "@/components/sentinel/SentinelConfig";
import { SentinelCycleRunner } from "@/components/sentinel/SentinelCycleRunner";
import { SentinelLoopFechado } from "@/components/sentinel/SentinelLoopFechado";

const VALID_TABS = SENTINEL_TABS.map((t) => t.value) as readonly string[];

export default function Sentinel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = useMemo<SentinelTab>(() => {
    const t = searchParams.get("tab");
    return (t && VALID_TABS.includes(t) ? t : "overview") as SentinelTab;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [tab, setTabState] = useState<SentinelTab>(initialTab);
  const [cycleOpen, setCycleOpen] = useState(false);

  const setTab = (t: SentinelTab) => {
    setTabState(t);
    const next = new URLSearchParams(searchParams);
    if (t === "overview") next.delete("tab");
    else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  // Sync when user navigates with browser back/forward
  useEffect(() => {
    const t = searchParams.get("tab");
    const valid = (t && VALID_TABS.includes(t) ? t : "overview") as SentinelTab;
    if (valid !== tab) setTabState(valid);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: orgs = [] } = useSentinelOrgs();
  const orgId = orgs[0]?.id;

  const runEvaluation = useRunEvaluation();
  const runAudit = useRunAudit();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Radar className="w-6 h-6 text-primary" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-bold font-headline text-foreground">Sentinel</h1>
            <p className="text-sm text-muted-foreground">
              Monitoramento proativo, auditoria e governança de IA
            </p>
          </div>
        </div>
        <RoleGate permission="sentinel.manage">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={runEvaluation.isPending}
              onClick={() => runEvaluation.mutate()}
            >
              {runEvaluation.isPending
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <Play className="w-4 h-4 mr-1.5" />}
              <span className="hidden sm:inline">Avaliar</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={runAudit.isPending || !orgId}
              onClick={() => orgId && runAudit.mutate({ org_id: orgId })}
            >
              {runAudit.isPending
                ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                : <Search className="w-4 h-4 mr-1.5" />}
              <span className="hidden sm:inline">Auditar</span>
            </Button>
            <Sheet open={cycleOpen} onOpenChange={setCycleOpen}>
              <SheetTrigger asChild>
                <Button size="sm" disabled={!orgId}>
                  <Workflow className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">Ciclo Completo</span>
                  <span className="sm:hidden">Ciclo</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Ciclo Sentinel</SheetTitle>
                </SheetHeader>
                <div className="mt-4">
                  {orgId && <SentinelCycleRunner orgId={orgId} />}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </RoleGate>
      </div>

      <SentinelLoopFechado />

      <Tabs value={tab} onValueChange={(v) => setTab(v as SentinelTab)} className="space-y-4">
        <TabsList className="flex-wrap h-auto gap-1 w-full sm:w-auto justify-start">
          {SENTINEL_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
          ))}
        </TabsList>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
          >
            <TabsContent value="overview" forceMount={tab === "overview" ? true : undefined}>
              {tab === "overview" && (
                <SentinelOverview orgId={orgId} onTabChange={(t) => setTab(t as SentinelTab)} />
              )}
            </TabsContent>
            <TabsContent value="alerts">{tab === "alerts" && <SentinelAlerts orgId={orgId} />}</TabsContent>
            <TabsContent value="audits">{tab === "audits" && <SentinelAudits orgId={orgId} />}</TabsContent>
            <TabsContent value="proposals">{tab === "proposals" && <SentinelProposals orgId={orgId} />}</TabsContent>
            <TabsContent value="forecasts">{tab === "forecasts" && <SentinelForecasts orgId={orgId} />}</TabsContent>
            <TabsContent value="reports">{tab === "reports" && <SentinelReports orgId={orgId} />}</TabsContent>
            <TabsContent value="config">{tab === "config" && <SentinelConfig orgId={orgId} />}</TabsContent>
          </motion.div>
        </AnimatePresence>
      </Tabs>
    </div>
  );
}
